package uz.ferro.shop.pages.about

import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import mui.material.Box
import mui.material.Grid
import mui.material.Table
import mui.material.TableBody
import mui.material.TableCell
import mui.material.TableHead
import mui.material.TableRow
import mui.system.responsive
import mui.system.sx
import react.FC
import react.Props
import react.useContext
import react.useEffectOnce
import react.useState
import uz.ferro.shop.AppContext
import uz.ferro.shop.Colors
import uz.ferro.shop.LocaleContext
import uz.ferro.shop.NavigationContext
import uz.ferro.shop.api.suspendGet
import uz.ferro.shop.document.view.FormattedTextView
import uz.ferro.shop.helmet.Helmet
import uz.ferro.shop.locale.LanguageManager
import uz.ferro.shop.model.DictItem
import uz.ferro.shop.pages.MarketPage
import uz.ferro.shop.ui.AppText
import uz.ferro.shop.ui.AppTextBold
import uz.ferro.shop.ui.AppTextMedium
import uz.ferro.shop.ui.AppTextSemiBold
import uz.ferro.shop.ui.HorizontalLayout
import uz.ferro.shop.ui.ImageView
import uz.ferro.shop.ui.StackLayout
import uz.ferro.shop.ui.VerticalLayout
import uz.ferro.shop.util.localizedOrDefault
import uz.ferro.shop.util.md
import uz.ferro.shop.util.sm
import uz.ferro.shop.util.styleInMobile
import uz.ferro.shop.util.twoDigitAtLeast
import uz.ferro.shop.util.xs
import web.cssom.AlignItems
import web.cssom.Auto
import web.cssom.BorderCollapse
import web.cssom.Color
import web.cssom.Cursor
import web.cssom.Display
import web.cssom.FontSize
import web.cssom.FontWeight
import web.cssom.JustifyContent
import web.cssom.LineStyle
import web.cssom.Position
import web.cssom.TableLayout
import web.cssom.TextAlign
import web.cssom.VerticalAlign
import web.cssom.integer
import web.cssom.number
import web.cssom.pct
import web.cssom.px

external interface SectionTitleProps : Props {
    var title: DictItem?
}

private val SectionTitle = FC<SectionTitleProps> { props ->
    AppTextBold {
        sx {
            fontSize = styleInMobile(18.px, 31.px)
            textAlign = TextAlign.center
            marginTop = styleInMobile(24.px, 48.px)
            display = Display.block
        }
        color = Colors.accentColor
        text = props.title.localizedOrDefault()
    }
}

external interface CircleTextProps : Props {
    var text: String
    var backgroundColor: Color
    var textColor: Color
    var size: Int
    var fontSize: FontSize
    var isBold: Boolean
}

private val CircleText = FC<CircleTextProps> { props ->
    VerticalLayout {
        sx {
            width = props.size.px
            height = props.size.px
            backgroundColor = props.backgroundColor
            borderRadius = (props.size / 2).px
            if (props.isBold) {
                fontWeight = FontWeight.bold
            }
            flexShrink = number(0.0)
            color = props.textColor
            alignItems = AlignItems.center
            justifyContent = JustifyContent.center
            fontSize = props.fontSize
        }
        +props.text
    }
}

val AboutPage = FC<Props> {
    val locale = useContext(LocaleContext)
    val navigator = useContext(NavigationContext)
    val appContext = useContext(AppContext)
    val isMobile = appContext.isMd.not()
    val isDesktop = appContext.isMd
    var data: AboutPageData? by useState()

    fun loadData() {
        MainScope().launch {
            try {
                data = suspendGet(path = "/data/about/about.json", modifyPath = false)
            } catch (e: Exception) {
                //
            }
        }
    }

    useEffectOnce {
        loadData()
    }

    MarketPage(
        hideCategory = true,
        hideMobileCatalog = true,
        fullBlock = {
            if (data != null) {
                Helmet {
                    title = locale.aboutCompany
                }
                Box {
                    sx {
                        marginTop = 16.px
                    }

                    StackLayout {
                        isHorizontal = isMobile.not()
                        sx {
                            alignItems = AlignItems.center
                        }

                        VerticalLayout {
                            space = styleInMobile(18.px, 24.px)
                            sx {
                                flexBasis = 0.px
                                flexGrow = number(1.0)
                                justifyContent = JustifyContent.center

                                if (isMobile) {
                                    alignItems = AlignItems.center
                                }
                            }
                            AppTextBold {
                                sx {
                                    fontSize = styleInMobile(18.px, 31.px)
                                }
                                color = Colors.accentColor
                                text = data?.aboutUS?.title.localizedOrDefault()
                            }

                            AppTextMedium {
                                sx {
                                    fontSize = styleInMobile(13.px, 20.px)
                                }
                                color = Colors.black
                                text = data?.aboutUS?.description.localizedOrDefault()
                            }
                        }

                        ImageView {
                            sx {
                                width = styleInMobile(306.px, 453.px)
                                height = Auto.auto
                                aspectRatio = number(1.0)
                            }
                            url = data?.aboutUS?.image
                        }
                    }



                    Box {
                        sx {
                            height = styleInMobile(16.px, 36.px)
                        }
                    }

                    Grid {
                        container = true
                        spacing = responsive(20.px)

                        data?.statistics?.items.orEmpty()
                            .forEach { pageItem ->
                                Grid {
                                    item = true
                                    xs = 6
                                    sm = 6
                                    md = 4

                                    sx {

                                    }

                                    VerticalLayout {
                                        space = styleInMobile(6.px, 10.px)
                                        HorizontalLayout {
                                            space = styleInMobile(8.px, 12.px)
                                            sx {
                                                height = styleInMobile(24.px, 48.px)
                                            }

                                            Box {
                                                sx {
                                                    width = styleInMobile(2.px, 5.px)
                                                    height = 100.pct
                                                    backgroundColor = Colors.accentColor
                                                }
                                            }

                                            ImageView {
                                                sx {
                                                    width = styleInMobile(24.px, 48.px)
                                                    height = styleInMobile(24.px, 48.px)
                                                }
                                                url = pageItem.image
                                            }

                                            AppTextBold {
                                                sx {
                                                    fontSize = styleInMobile(18.px, 31.px)
                                                }
                                                text = pageItem.title.localizedOrDefault()
                                            }
                                        }

                                        AppText {
                                            sx {
                                                paddingLeft = styleInMobile(10.px, 18.px)
                                                fontSize = styleInMobile(13.px, 20.px)
                                            }
                                            text = pageItem.description.localizedOrDefault()
                                        }
                                    }
                                }
                            }
                    }

                    Box {
                        sx {
                            height = styleInMobile(16.px, 36.px)
                        }
                    }

                    AboutMissionsAndStrategy {
                        this.data = data!!
                    }

                    SectionTitle { title = data?.achievements?.title }

                    StackLayout {
                        isHorizontal = isMobile.not()
                        space = styleInMobile(16.px, 80.px)
                        sx {
                            marginTop = styleInMobile(20.px, 36.px)
                            alignItems = AlignItems.center
                        }

                        StackLayout {
                            isHorizontal = isMobile
                            space = styleInMobile(25.px, 50.px)
                            sx {
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                            }

                            ImageView {
                                sx {
                                    width = styleInMobile(183.px, 297.px)
                                    height = styleInMobile(91.px, 147.px)
                                }
                                url = data?.achievements?.items?.firstOrNull()?.image
                            }

                            ImageView {
                                sx {
                                    width = styleInMobile(120.px, 148.px)
                                    height = styleInMobile(211.px, 260.px)
                                }
                                url = data?.achievements?.items?.getOrNull(1)?.image
                            }
                        }

                        VerticalLayout {
                            space = 24.px
                            data?.achievements?.items?.forEach { item ->
                                FormattedTextView {
                                    sx {
                                        if (isMobile) {
                                            textAlign = TextAlign.center
                                        }
                                        fontSize = styleInMobile(13.px, 20.px)
                                    }
                                    formattedText = item.formattedDescription.localizedOrDefault()
                                }
                            }
                        }
                    }

                    SectionTitle { title = data?.structure?.title }

                    Box {
                        sx {
                            height = styleInMobile(12.px, 36.px)
                        }
                    }

                    Grid {
                        container = true
                        spacing = styleInMobile(16.px, 36.px)

                        data?.structure?.items?.forEachIndexed { index, itm ->
                            Grid {
                                item = true
                                xs = 6

                                StackLayout {
                                    isHorizontal = isMobile.not()
                                    space = styleInMobile(6.px, 24.px)

                                    CircleText {
                                        text = (index + 1).toString()
                                        size = if (isMobile) 24 else 60
                                        backgroundColor = Colors.accentColor
                                        textColor = Colors.accentContrastColor
                                        isBold = true
                                        fontSize = styleInMobile(13.px, 31.px)
                                    }

                                    VerticalLayout {
                                        space = styleInMobile(4.px, 16.px)
                                        AppTextSemiBold {
                                            color = Colors.black
                                            text = itm.title.localizedOrDefault()
                                            sx {
                                                fontSize = styleInMobile(13.px, 24.px)
                                            }
                                        }

                                        AppTextMedium {
                                            color = Colors.black
                                            text = itm.description.localizedOrDefault()
                                            sx {
                                                fontSize = styleInMobile(13.px, 20.px)
                                            }
                                        }
                                    }

                                }
                            }
                        }
                    }

                    SectionTitle { title = data?.whsAndDist?.title }

                    VerticalLayout {
                        sx {
                            marginTop = styleInMobile(16.px, 36.px)
                        }

                        data?.whsAndDist?.items?.forEachIndexed { index, itm ->
                            if (isDesktop) {
                                HorizontalLayout {
                                    sx {
                                        alignItems = AlignItems.center
                                    }
                                    if (index % 2 == 0) {
                                        ImageView {
                                            sx {
                                                aspectRatio = number(1240.0 / 756)
                                                flexGrow = number(1.0)
                                                flexBasis = 0.px
                                            }
                                            url = itm.image
                                        }
                                        VerticalLayout {
                                            sx {
                                                flexGrow = number(1.0)
                                                flexBasis = 0.px
                                                alignItems = AlignItems.center
                                                justifyContent = JustifyContent.center
                                            }
                                            AppTextMedium {
                                                sx {
                                                    fontSize = styleInMobile(13.px, 20.px)
                                                    paddingLeft = 12.pct
                                                    paddingRight = 12.pct
                                                    paddingTop = 24.px
                                                    paddingBottom = 24.px
                                                }
                                                text = itm.description.localizedOrDefault()
                                            }
                                        }
                                    } else {
                                        VerticalLayout {
                                            sx {
                                                flexGrow = number(1.0)
                                                flexBasis = 0.px
                                                alignItems = AlignItems.center
                                                justifyContent = JustifyContent.center
                                            }
                                            AppTextMedium {
                                                sx {
                                                    fontSize = styleInMobile(13.px, 20.px)
                                                    paddingLeft = 12.pct
                                                    paddingRight = 12.pct
                                                    paddingTop = 24.px
                                                    paddingBottom = 24.px
                                                }
                                                text = itm.description.localizedOrDefault()
                                            }
                                        }

                                        ImageView {
                                            sx {
                                                aspectRatio = number(1240.0 / 756)
                                                flexGrow = number(1.0)
                                                flexBasis = 0.px
                                            }
                                            url = itm.image
                                        }
                                    }
                                }
                            } else {
                                ImageView {
                                    sx {
                                        width = 100.pct
                                        aspectRatio = number(1240.0 / 756)
                                    }
                                    url = itm.image
                                }

                                AppTextMedium {
                                    sx {
                                        fontSize = styleInMobile(13.px, 20.px)
                                        paddingTop = 8.px
                                        paddingBottom = 24.px
                                    }
                                    text = itm.description.localizedOrDefault()
                                }
                            }
                        }
                    }

                    ImageView {
                        sx {
                            if (isDesktop) {
                                marginTop = 48.px
                            }
                            width = 100.pct
                            height = Auto.auto
                        }
                        url = data?.whsAndDist?.image
                    }

                    SectionTitle { title = data?.productRange?.title }

                    HorizontalLayout {
                        sx {
                            marginTop = styleInMobile(16.px, 36.px)
                            paddingLeft = styleInMobile(0.px, 80.px)
                            paddingRight = styleInMobile(0.px, 80.px)
                        }

                        FormattedTextView {
                            sx {
                                fontSize = styleInMobile(13.px, 20.px)
                                color = Colors.black
                                textAlign = TextAlign.center
                            }
                            formattedText = data?.productRange?.formattedDescription.localizedOrDefault()
                        }
                    }

                    Box {
                        sx {
                            height = styleInMobile(16.px, 36.px)
                        }
                    }

                    Grid {
                        container = true
                        spacing = styleInMobile(8.px, 20.px)

                        data?.productRange?.items?.forEachIndexed { index, itm ->
                            Grid {
                                item = true

                                xs = 12
                                sm = 12
                                md = 6

                                HorizontalLayout {
                                    sx {
                                        height = styleInMobile(48.px, 80.px)
                                        backgroundColor = Colors.black
                                        borderRadius = styleInMobile(24.px, 40.px)
                                        alignItems = AlignItems.center
                                        cursor = Cursor.pointer
                                    }

                                    onClick = {
                                        navigator.openUrl(itm.link.orEmpty())
                                        window.scroll(0.0, 0.0)
                                    }

                                    CircleText {
                                        text = (index + 1).twoDigitAtLeast()
                                        size = if (isMobile) 48 else 80
                                        backgroundColor = Colors.accentColor
                                        textColor = Colors.accentContrastColor
                                        fontSize = styleInMobile(13.px, 24.px)
                                    }

                                    Box {
                                        sx {
                                            width = styleInMobile(60.px, 100.px)
                                        }
                                    }

                                    ImageView {
                                        sx {
                                            width = styleInMobile(24.px, 40.px)
                                            height = styleInMobile(24.px, 40.px)
                                        }
                                        url = itm.image
                                    }

                                    Box {
                                        sx {
                                            width = 8.px
                                        }
                                    }

                                    AppTextMedium {
                                        text = itm.title.localizedOrDefault()
                                        color = Colors.white
                                        sx {
                                            fontSize = styleInMobile(13.px, 24.px)
                                        }
                                    }
                                }

                            }
                        }
                    }

                    Box {
                        sx {
                            height = styleInMobile(16.px, 36.px)
                        }
                    }

                    Box {
                        sx {
                            width = 100.pct
                            aspectRatio = if (isDesktop) {
                                number(2 * 1240.0 / 800)
                            } else {
                                number(0.6 * 2 * 1240.0 / 800)
                            }
                            position = Position.relative
                        }

                        HorizontalLayout {
                            sx {
                                position = Position.absolute
                                width = 100.pct
                                height = 100.pct
                                alignItems = AlignItems.stretch
                                justifyContent = JustifyContent.stretch
                            }

                            ImageView {
                                sx {
                                    flexGrow = number(1.0)
                                    flexBasis = 0.px
                                    height = 100.pct
                                }

                                isCovered = true
                                url = data?.flagsSection?.items?.firstOrNull()?.image
                            }

                            ImageView {
                                sx {
                                    flexGrow = number(1.0)
                                    flexBasis = 0.px
                                    height = 100.pct
                                }
                                isCovered = true
                                url = data?.flagsSection?.items?.getOrNull(1)?.image
                            }
                        }

                        VerticalLayout {
                            sx {
                                position = Position.absolute
                                width = 100.pct
                                height = 100.pct
                                fontSize = styleInMobile(13.px, 20.px)
                                color = Colors.white
                                fontWeight = integer(500)
                                alignItems = AlignItems.center
                                justifyContent = JustifyContent.center
                                textAlign = TextAlign.center
                                paddingLeft = styleInMobile(16.px, 120.px)
                                paddingRight = styleInMobile(16.px, 120.px)
                                paddingTop = 8.px
                                paddingBottom = 8.px
                                lineHeight = 120.pct
                            }

                            +data?.flagsSection?.description.localizedOrDefault()
                        }
                    }
                }


                SectionTitle {
                    title = data?.whyChoose?.title
                }

                AppText {
                    sx {
                        fontSize = styleInMobile(13.px, 20.px)
                        lineHeight = 120.pct
                        marginTop = styleInMobile(16.px, 36.px)
                        textAlign = TextAlign.center
                        color = Colors.black
                    }

                    text = data?.whyChoose?.description.localizedOrDefault()
                }

                val groupedTableItems = if (isDesktop) {
                    listOf(data?.whyChoose?.items.orEmpty())
                } else {
                    data?.whyChoose?.items.orEmpty().chunked(2)
                }

                VerticalLayout {
                    space = 12.px
                    sx {
                        marginTop = styleInMobile(16.px, 36.px)
                    }

                    groupedTableItems.forEach { items ->
                        Table {
                            sx {
                                tableLayout = TableLayout.fixed
                            }
                            TableHead {
                                TableRow {
                                    items.forEach { itm ->
                                        TableCell {
                                            sx {
                                                width = 100.pct
                                                height = styleInMobile(96.px, 200.px)
                                                padding = styleInMobile(16.px, 24.px)
                                                textAlign = TextAlign.center
                                                verticalAlign = VerticalAlign.middle
                                                borderStyle = LineStyle.solid
                                                borderWidth = 1.px
                                                borderCollapse = BorderCollapse.collapse
                                                borderColor = Colors.gray05
                                                backgroundColor = Colors.gray02
                                            }
                                            StackLayout {
                                                isHorizontal = isDesktop
                                                space = styleInMobile(6.px, 12.px)
                                                sx {
                                                    alignItems = AlignItems.center
                                                    justifyContent = JustifyContent.center
                                                }

                                                ImageView {
                                                    sx {
                                                        width = styleInMobile(24.px, 48.px)
                                                        height = styleInMobile(24.px, 48.px)
                                                    }
                                                    url = itm.image
                                                }

                                                AppTextBold {
                                                    sx {
                                                        lineHeight = 120.pct
                                                        fontSize = styleInMobile(13.px, 20.px)
                                                    }
                                                    color = Colors.black
                                                    text = itm.title.localizedOrDefault()
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            TableBody {
                                TableRow {
                                    items.forEach { itm ->
                                        TableCell {
                                            sx {
                                                width = 100.pct
                                                height = styleInMobile(70.px, 200.px)
                                                fontSize = styleInMobile(13.px, 20.px)
                                                padding = styleInMobile(8.px, 24.px)
                                                textAlign = TextAlign.center
                                                verticalAlign = VerticalAlign.middle
                                                borderStyle = LineStyle.solid
                                                borderColor = Colors.gray05
                                                borderWidth = 1.px
                                                borderCollapse = BorderCollapse.collapse
                                            }
                                            +itm.description.localizedOrDefault()
                                        }
                                    }
                                }
                            }
                        }
                    }
                }


                SectionTitle {
                    title = data?.stores?.title
                }

                ImageView {
                    sx {
                        width = 100.pct
                        aspectRatio = number(1240.0 / 692)
                        marginTop = styleInMobile(16.px, 36.px)
                    }
                    url = data?.stores?.image
                }

                AppTextMedium {
                    sx {
                        fontSize = styleInMobile(13.px, 20.px)
                        textAlign = TextAlign.center
                        marginTop = styleInMobile(16.px, 36.px)
                    }
                    text = data?.stores?.description.localizedOrDefault()
                }

                ImageView {
                    sx {
                        width = 100.pct
                        aspectRatio = number(1240.0 / 570)
                        marginTop = styleInMobile(16.px, 36.px)
                    }
                    url = data?.stores?.items?.firstOrNull()?.image
                }

                SectionTitle {
                    title = data?.whereFind?.title
                }

                StackLayout {
                    isHorizontal = isDesktop
                    sx {
                        marginTop = styleInMobile(16.px, 36.px)
                    }

                    VerticalLayout {
                        sx {
                            if (isDesktop) {
                                flexGrow = number(1.0)
                                flexShrink = number(1.0)
                                flexBasis = 0.px
                            }

                            padding = styleInMobile(16.px, 24.px)
                            backgroundColor = Colors.gray02
                            alignItems = AlignItems.center
                            justifyContent = JustifyContent.center
                        }

                        Grid {
                            spacing = styleInMobile(8.px, 24.px)
                            container = true
                            data?.whereFind?.items?.forEach { itm ->
                                Grid {
                                    item = true
                                    xs = 6

                                    sx {
                                        minHeight = styleInMobile(56.px, 72.px)
                                    }

                                    HorizontalLayout {
                                        space = 8.px

                                        ImageView {
                                            sx {
                                                flexShrink = number(0.0)
                                                width = styleInMobile(24.px, 32.px)
                                            }
                                            url = itm.image
                                        }

                                        AppTextMedium {
                                            sx {
                                                paddingTop = styleInMobile(2.px, 2.px)
                                                fontSize = styleInMobile(13.px, 20.px)
                                            }
                                            text = itm.title.localizedOrDefault()
                                        }
                                    }
                                }
                            }
                        }
                    }

                    ImageView {
                        sx {
                            if (isMobile) {
                                width = 100.pct
                            } else {
                                flexGrow = number(1.0)
                                flexShrink = number(1.0)
                                flexBasis = 0.px
                            }
                            aspectRatio = number(720.0 / 444)
                        }
                        url = data?.whereFind?.image
                    }
                }
            }
        }
    ).invoke()
}

